export type IdeaState = "Capturing" | "Scanning" | "Critiqued" | "SpecReady";

export interface Clarification {
  question: string;
  answer: string;
  timestampMs: number;
}

export interface ScanProduct {
  name: string;
  tagline: string;
  rationale: string;
}

export interface ScanResult {
  scannedAtMs: number;
  source: "llm-brainstorm" | "product-hunt";
  searchQueries: string[];
  products: ScanProduct[];
}

export interface Persona {
  id: string;
  name: string;
  descriptor: string;
  voiceSignature: string;
  evidenceSource: string;
  jobsToBeDone: string;
  currentAlternative: string;
  dealbreakers: string[];
  mustHaves: string[];
}

export interface Critique {
  personaId: string;
  problemFitScore: number;
  switchLikelihoodScore: number;
  reactionSummary: string;
  questionsBack: string[];
  dealbreakers: string[];
  mustHaves: string[];
}

export interface BuildSpec {
  generatedAtMs: number;
  productNameSuggestion: string;
  markdown: string;
  source: string;
}

export interface Idea {
  id: string;
  title: string;
  rawCapture: string;
  state: IdeaState;
  createdAtMs: number;
  updatedAtMs: number;
  /**
   * Mom-Test clarifying Q&A captured one at a time during the Clarify flow.
   * Older records (pre-step-6) may not have this field; readers default to [].
   */
  clarifications?: Clarification[];
  scanResult?: ScanResult;
  personas?: Persona[];
  critiques?: Critique[];
  buildSpecs?: BuildSpec[];
}

export type CloudProvider = "Anthropic" | "OpenAI" | "Gemini";

export const CLOUD_PROVIDERS: readonly CloudProvider[] = [
  "Anthropic",
  "OpenAI",
  "Gemini",
] as const;

export const CLOUD_PROVIDER_DISPLAY: Record<CloudProvider, string> = {
  Anthropic: "Anthropic Claude",
  OpenAI: "OpenAI",
  Gemini: "Google Gemini",
};

export const CLOUD_PROVIDER_KEY_HINT: Record<CloudProvider, string> = {
  Anthropic: "sk-ant-…",
  OpenAI: "sk-…",
  Gemini: "AIza…",
};

export interface Settings {
  cloudEnabled: boolean;
  selectedProvider: CloudProvider;
  apiKeys: Partial<Record<CloudProvider, string>>;
  huggingFaceToken: string;
  customModelUrl: string;
  customModelFileName: string;
}

export const DEFAULT_SETTINGS: Settings = {
  cloudEnabled: false,
  selectedProvider: "Anthropic",
  apiKeys: {},
  huggingFaceToken: "",
  customModelUrl: "",
  customModelFileName: "",
};

export interface ModelConfig {
  url: string;
  fileName: string;
  displayName: string;
}

export const DEFAULT_MODEL_CONFIG: ModelConfig = {
  url: "https://huggingface.co/litert-community/gemma-4-E2B-it-litert-lm/resolve/main/gemma-4-E2B-it.litertlm?download=true",
  fileName: "gemma-4-E2B-it.litertlm",
  displayName: "Gemma 4 E2B (LiteRT-LM)",
};

export function effectiveModelConfig(settings: Settings): ModelConfig {
  const url = settings.customModelUrl.trim() || DEFAULT_MODEL_CONFIG.url;
  const fileName = settings.customModelFileName.trim() || DEFAULT_MODEL_CONFIG.fileName;
  return { url, fileName, displayName: DEFAULT_MODEL_CONFIG.displayName };
}

export function activeKey(settings: Settings): string {
  return settings.apiKeys[settings.selectedProvider] ?? "";
}

export function isReadyForCloud(settings: Settings): boolean {
  return settings.cloudEnabled && activeKey(settings).trim().length > 0;
}

export function newIdea(input: { title: string; rawCapture: string }): Idea {
  const now = Date.now();
  return {
    id: `idea-${now}-${Math.floor(Math.random() * 0xffff).toString(16).padStart(4, "0")}`,
    title: input.title.trim(),
    rawCapture: input.rawCapture.trim(),
    state: "Capturing",
    createdAtMs: now,
    updatedAtMs: now,
    clarifications: [],
  };
}

export function clarificationsOf(idea: Idea): Clarification[] {
  return idea.clarifications ?? [];
}

export function scanResultOf(idea: Idea): ScanResult | undefined {
  return idea.scanResult;
}

export function personasOf(idea: Idea): Persona[] {
  return idea.personas ?? [];
}

export function critiquesOf(idea: Idea): Critique[] {
  return idea.critiques ?? [];
}

export function latestBuildSpec(idea: Idea): BuildSpec | undefined {
  const specs = idea.buildSpecs ?? [];
  if (specs.length === 0) return undefined;
  return specs[specs.length - 1];
}

export function ideaStateLabel(state: IdeaState): string {
  switch (state) {
    case "Capturing":
      return "Capturing";
    case "Scanning":
      return "Scanning Product Hunt";
    case "Critiqued":
      return "Personas critiqued";
    case "SpecReady":
      return "Spec ready";
  }
}
